import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    Proposal,
    SelectedProposal,
} from 'entities/Proposal/model/types/ProposalSchema';
import { getPropertyVendorsMap } from 'entities/Vendors/model/selectors/vendorsSelectors';
import { SupplierLogo } from 'shared/ui/SupplierLogo';

const MAX_LOGOS_TO_DISPLAY = 3;
const MAX_NAMES_TO_DISPLAY = 2;
const LOGO_SIZE = 18;

interface SuppliersForItemProps {
    selectedProposals: SelectedProposal[];
    showEmptyProposals?: boolean;
    opacity?: number;
    proposals?: Proposal[];
    subtitle?: string;
}

const getSelectedQuantity = (
    proposal: Proposal | SelectedProposal,
    showEmptyProposals: boolean,
) => {
    if (showEmptyProposals) {
        return 0;
    }

    return (proposal as SelectedProposal).selectedQuantity ?? 0;
};

const getSellerUid = (
    proposal: Proposal | SelectedProposal,
    showEmptyProposals: boolean,
) => {
    if (showEmptyProposals) {
        return (proposal as Proposal).seller.uid;
    }

    return (proposal as SelectedProposal).sellerUid;
};

const getName = (
    proposal: Proposal | SelectedProposal,
    showEmptyProposals: boolean,
) => {
    if (showEmptyProposals) {
        return (proposal as Proposal).seller.name;
    }

    return (proposal as SelectedProposal).name;
};

export const SuppliersForItem = ({
    selectedProposals,
    showEmptyProposals = false,
    opacity = 1,
    proposals,
    subtitle,
}: SuppliersForItemProps) => {
    const { t } = useTranslation();
    const vendorsMap = useSelector(getPropertyVendorsMap);

    const logoCountToDisplay = showEmptyProposals ? 1 : MAX_LOGOS_TO_DISPLAY;
    const nameCountToDisplay = showEmptyProposals ? 1 : MAX_NAMES_TO_DISPLAY;

    const validProposals = useMemo(() => {
        const proposalSource: (Proposal | SelectedProposal)[] =
            showEmptyProposals
                ? proposals?.slice(0, 1) || []
                : selectedProposals;

        return proposalSource
            .filter(
                (proposal: Proposal | SelectedProposal) =>
                    getSelectedQuantity(proposal, showEmptyProposals) > 0 ||
                    showEmptyProposals,
            )
            .sort(
                (
                    a: Proposal | SelectedProposal,
                    b: Proposal | SelectedProposal,
                ) =>
                    getSelectedQuantity(a, showEmptyProposals) -
                    getSelectedQuantity(b, showEmptyProposals),
            );
    }, [proposals, selectedProposals, showEmptyProposals]);

    const logosToDisplay = useMemo(
        () =>
            validProposals
                ?.map(
                    (proposal: Proposal | SelectedProposal, index: number) => {
                        if (index >= logoCountToDisplay) {
                            return null;
                        }

                        const vendor =
                            vendorsMap?.[
                                getSellerUid(proposal, showEmptyProposals)
                            ];
                        if (!vendor) {
                            return null;
                        }

                        return (
                            <SupplierLogo
                                key={proposal.uid}
                                vendor={vendor}
                                size={LOGO_SIZE}
                                sx={{ marginLeft: index > 0 ? '-3px' : '0px' }}
                            />
                        );
                    },
                )
                .filter(Boolean),
        [validProposals, vendorsMap, logoCountToDisplay, showEmptyProposals],
    );

    const textToDisplay = useMemo(() => {
        if (validProposals?.length === 0) {
            return null;
        }

        if (validProposals?.length > nameCountToDisplay) {
            return `${validProposals?.length} ${t('suppliers')}`;
        }

        return validProposals
            .map((proposal: Proposal | SelectedProposal, index: number) => {
                if (index >= nameCountToDisplay) {
                    return null;
                }

                return getName(proposal, showEmptyProposals);
            })
            .filter(Boolean)
            .join(', ');
    }, [validProposals, t, nameCountToDisplay, showEmptyProposals]);

    if (validProposals?.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '18px',
                justifyContent: 'center',
                gap: '6px',
            }}
        >
            <Box sx={{ display: 'flex', gap: '9px' }}>
                <Box sx={{ display: 'flex', gap: '0px', opacity }}>
                    {logosToDisplay}
                </Box>
                <Box
                    sx={{
                        color: '#000',
                        fontSize: '13px',
                        fontWeight: 400,
                        lineHeight: 'normal',
                        opacity,
                    }}
                >
                    {textToDisplay}
                </Box>
            </Box>
            {subtitle && (
                <Box
                    sx={{
                        color: '#F00',
                        fontSize: '11px',
                        fontWeight: 400,
                        lineHeight: '14px',
                    }}
                >
                    {subtitle}
                </Box>
            )}
        </Box>
    );
};
